import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Disclaimer from '../components/Disclaimer';
import { isIOS } from '../utils/platform';

const TermsAndConditions = () => {
  // ────────────────────────────────────────────────────────────────────
  // Apple App Review Guideline 3.1.1 (Anti-Steering)
  //
  // Inside an iOS build, this Terms page MUST NOT compare the App Store
  // price to prices on other platforms, MUST NOT mention Stripe or any
  // other external payment processor, and MUST NOT direct users to an
  // external checkout URL. Apple reviewers routinely open the Terms page
  // during compliance review — the previous copy (which listed "Web /
  // Android / Windows — $7.77 USD / month, billed by Stripe" side-by-side
  // with the iOS price) is textbook steering language and would trigger
  // an immediate re-rejection.
  //
  // On non-iOS builds (web, Google Play, Microsoft Store) we still show
  // the full multi-platform breakdown — those stores REQUIRE billing
  // transparency and permit third-party payment processors.
  // ────────────────────────────────────────────────────────────────────
  const iosMode = isIOS();

  return (
    <div className="min-h-screen bg-[var(--sd-bg)] flex flex-col">
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="mb-6 text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Button>
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <FileText size={32} className="text-[var(--sd-primary)]" />
            <div>
              <h1 className="text-4xl font-semibold text-[var(--sd-fg)]">Terms and Conditions</h1>
              <p className="text-[var(--sd-muted-fg)] mt-2">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none space-y-8 text-[var(--sd-fg)]">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">1. Agreement to Terms</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                By accessing or using Selah Downloads ("Service," "Platform," "we," "us," or "our"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">2. Description of Service</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Selah Downloads is a spiritual insights platform that uses artificial intelligence to help you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Capture and organize your spiritual reflections</li>
                <li>Receive AI-generated spiritual insights and categorization</li>
                <li>Access relevant Bible verses for your journey</li>
                <li>Generate personalized activation prayers</li>
                <li>Track recurring spiritual themes over time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">3.1 Account Creation</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                To use our Service, you must create an account by providing:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>A valid email address</li>
                <li>A secure password</li>
                <li>Your name</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">3.2 Account Security</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">3.3 Account Eligibility</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                You must be at least 13 years old to use this Service. By creating an account, you represent that you meet this age requirement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">4. Free Trial &amp; Subscription</h2>

              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">4.1 Free Trial</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We offer a 7-day free trial period for new users. The trial automatically ends after 7 days unless you choose to subscribe. If you subscribe during the trial, your paid subscription begins at the end of the trial period.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.2 Subscription Options &amp; Billing</h3>
              {iosMode ? (
                <>
                  <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-2">
                    Selah Downloads Premium is an auto-renewable subscription.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                    <li>
                      <strong>$7.79 USD / month</strong>, billed by Apple through your Apple ID via Apple In-App Purchase.
                    </li>
                  </ul>
                  <p className="leading-relaxed text-[var(--sd-muted-fg)] mt-3">
                    Prices in currencies other than USD are set by Apple at approximate parity with the USD price and may vary due to exchange rates and local taxes.
                  </p>
                </>
              ) : (
                <>
                  <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-2">
                    Selah Downloads Premium is an auto-renewable subscription. The applicable price and billing method depend on where you subscribe:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                    <li>
                      <strong>iOS (iPhone / iPad) — $7.79 USD / month</strong>, billed by Apple through your Apple ID via Apple In-App Purchase.
                    </li>
                    <li>
                      <strong>Web, Android, and Windows — $7.77 USD / month</strong>, billed by Stripe.
                    </li>
                  </ul>
                  <p className="leading-relaxed text-[var(--sd-muted-fg)] mt-3">
                    Prices in currencies other than USD are set by the platform operator (Apple, Google, Microsoft, or Stripe) at approximate parity with the USD price and may vary due to exchange rates and local taxes.
                  </p>
                </>
              )}

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.3 Auto-Renewal (Apple In-App Purchase — iPhone / iPad)</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-2">
                When you subscribe on iPhone or iPad, the following terms apply as required by Apple:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Payment will be charged to your <strong>Apple ID account</strong> at confirmation of purchase.</li>
                <li>Your subscription <strong>automatically renews</strong> unless auto-renew is turned off at least 24 hours before the end of the current period.</li>
                <li>Your Apple ID will be charged for renewal within 24 hours prior to the end of the current period, at the price of the selected plan ($7.79 USD/month or local equivalent).</li>
                <li>You can <strong>manage your subscription</strong> and turn off auto-renewal at any time by going to your device's <strong>Settings → your Apple ID → Subscriptions</strong> after purchase.</li>
                <li>Any unused portion of the free trial will be forfeited if you purchase a subscription during the trial period, where applicable.</li>
                <li>Payment for iOS subscriptions is handled entirely by Apple; Selah Downloads never sees or stores your payment card information.</li>
              </ul>

              {!iosMode && (
                <>
                  <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.4 Auto-Renewal (Web, Android, Windows via Stripe)</h3>
                  <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                    Subscriptions purchased on the web at selahdownloads.com, via Google Play, or via the Microsoft Store are processed by Stripe and renew monthly at $7.77 USD (or local equivalent) until cancelled. You may cancel at any time from <strong>Settings → Manage Subscription</strong> inside the app or web experience. Access continues through the end of the current billing period; there is no proration.
                  </p>
                </>
              )}

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.5 Cancellation</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                {iosMode ? (
                  <>
                    Cancel via iPhone Settings → your Apple ID → Subscriptions → Selah Downloads → Cancel Subscription. Cancellation takes effect at the end of the current billing period. Deleting the app does not cancel the subscription.
                  </>
                ) : (
                  <>
                    <strong>iOS subscribers:</strong> cancel via iPhone Settings → your Apple ID → Subscriptions → Selah Downloads → Cancel Subscription. Cancellation takes effect at the end of the current billing period. Deleting the app does not cancel the subscription.
                    <br /><br />
                    <strong>All other subscribers:</strong> cancel via Settings → Manage Subscription inside Selah Downloads. Cancellation takes effect at the end of the current billing period.
                  </>
                )}
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.6 Refunds</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                {iosMode ? (
                  <>
                    Refunds are handled exclusively by Apple. Request a refund at{' '}
                    <a
                      href="https://reportaproblem.apple.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--sd-primary)] hover:underline"
                    >
                      reportaproblem.apple.com
                    </a>.
                  </>
                ) : (
                  <>
                    <strong>iOS subscribers:</strong> refunds are handled exclusively by Apple. Request a refund at{' '}
                    <a
                      href="https://reportaproblem.apple.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--sd-primary)] hover:underline"
                    >
                      reportaproblem.apple.com
                    </a>.
                    <br /><br />
                    <strong>All other subscribers:</strong> please email support@selahdownloads.com within 14 days of your most recent renewal for a refund review. Refunds are granted at our reasonable discretion in accordance with applicable consumer protection law.
                  </>
                )}
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">4.7 Apple Standard EULA</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                {iosMode ? (
                  <>
                    Your use of Selah Downloads on this device is also subject to Apple's Standard End-User License Agreement, available at{' '}
                  </>
                ) : (
                  <>
                    When you install Selah Downloads on an Apple device, your use of the app is also subject to Apple's Standard End-User License Agreement, available at{' '}
                  </>
                )}
                <a
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--sd-primary)] hover:underline"
                >
                  apple.com/legal/internet-services/itunes/dev/stdeula
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">5. User Content</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">5.1 Your Spiritual Downloads</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                You retain all ownership rights to the content you create ("User Content"). By using our Service, you grant us a limited license to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Store your encrypted downloads on our servers</li>
                <li>Process your content through AI services to generate insights</li>
                <li>Provide you with spiritual guidance and Bible verses</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">5.2 Content Guidelines</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                You agree not to use our Service to create, upload, or share content that:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Is illegal, harmful, or violates any laws</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains hate speech, harassment, or discrimination</li>
                <li>Promotes violence or self-harm</li>
                <li>Is deceptive or fraudulent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">6. AI-Generated Content</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">6.1 Nature of AI Insights</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                The spiritual insights, categorizations, Bible verses, and prayers generated by our AI are meant to supplement, not replace, your personal faith journey, pastoral guidance, or professional counseling.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">6.2 No Guarantee of Accuracy</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                While we strive for accuracy, AI-generated content may contain errors or interpretations that differ from your theological understanding. We encourage you to verify Bible verses and seek wisdom from trusted spiritual leaders.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">6.3 Not Professional Advice</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Our Service does not provide professional counseling, therapy, or medical advice. If you are experiencing a mental health crisis, please contact a qualified professional or emergency services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">7. Privacy & Data Security</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                Your privacy is important to us. Please review our <Link to="/privacy" className="text-[var(--sd-primary)] hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your data. Key points:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4 mt-4">
                <li>All downloads are encrypted client-side before storage</li>
                <li>Only you can decrypt your personal spiritual content</li>
                <li>We never sell or share your personal data with third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">8. Prohibited Uses</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)] mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[var(--sd-muted-fg)] ml-4">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Scrape or harvest data from the Service</li>
                <li>Impersonate others or create fake accounts</li>
                <li>Interfere with the proper functioning of the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">9. Intellectual Property</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                The Service, including its design, features, text, graphics, logos, and software, is owned by Selah &amp; Stone Legacy Holdings LLC and protected by copyright, trademark, and other intellectual property laws. Selah Downloads™, Selah Slate™, Selah Rest™, and Selah &amp; Salt Home™ are proprietary trademarks of Selah &amp; Stone Legacy Holdings LLC. You may not copy, modify, distribute, reverse engineer, or create derivative works of the Service or its underlying codebase without our prior written permission. Unauthorized reproduction or reverse engineering of this software codebase is strictly prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">10. Termination</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-[var(--sd-fg)]">10.1 By You</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                You may delete your account at any time from the Settings page. This will permanently remove all your data from our servers.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-[var(--sd-fg)]">10.2 By Us</h3>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We reserve the right to suspend or terminate your account if you violate these Terms or engage in behavior that harms the Service or other users.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">11. Disclaimers</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">12. Limitation of Liability</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SELAH DOWNLOADS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">13. Changes to Terms</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">14. Governing Law</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                These Terms are governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-[var(--sd-primary)]">15. Contact Us</h2>
              <p className="leading-relaxed text-[var(--sd-muted-fg)]">
                If you have questions about these Terms, please contact us at:
              </p>
              <p className="leading-relaxed text-[var(--sd-primary)] mt-4">
                <strong>Email:</strong> support@selahdownloads.com<br />
                <strong>Website:</strong> www.selahdownloads.com
              </p>
              <address
                className="not-italic leading-relaxed text-[var(--sd-muted-fg)] mt-4 text-sm"
                data-testid="terms-business-address"
              >
                <strong className="text-[var(--sd-fg-secondary)]">Mailing address:</strong><br />
                Selah &amp; Stone Legacy Holdings LLC<br />
                5005 W. Laurel, Ste. 100<br />
                Tampa, FL 33607, USA
              </address>
            </section>
          </div>
        </motion.div>
      </div>
      <Disclaimer />
    </div>
  );
};

export default TermsAndConditions;
